import Box from '@mui/material/Box';
import { ContentObjectOptions, type ContentObjectProps } from '@openx/types/targeting/contentObject.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { Fragment, type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DimensionChipLine } from '../../shared/DimensionChip/DimensionChipLine.tsx';
import { EmptyFilterMessage } from '../../shared/EmptyFilterMessage/EmptyFilterMessage.tsx';
import { Switch } from '../../shared/Switch/Switch.tsx';
import type { TargetingItemsProps } from '../../types.ts';
import { filterEmptyParams, getExcludeAnyWarning } from '../utils/utils.ts';
import { DefaultItems } from './components/DefaultItems.tsx';
import { HierarchyItems } from './components/HierarchyItems.tsx';
import { IntersectItems } from './components/IntersectItems.tsx';
import { IntersectOptionsItems } from './components/IntersectOptionsItems.tsx';
import { ExcludeAnyAlert } from './ExcludeAnyAlert.tsx';
import { EpisodeItems } from './episode/EpisodeItems.tsx';

interface ContentObjectItemsProps extends TargetingItemsProps<ContentObjectProps> {
  dataTest?: string;
}

export const ContentObjectItems = ({
  targetingParams,
  readonly,
  onChange = noop,
}: ContentObjectItemsProps): JSX.Element => {
  const { t } = useTranslation();

  const switchState = targetingParams[ContentObjectOptions.CONTENT_INTER_DIMENSION_OPERATOR] === Criteria.ANY;

  const dimensionChipLine = useMemo(
    () => <DimensionChipLine switchState={switchState} isSubsection={switchState} />,
    [switchState]
  );

  const shouldDisplayAlert = useMemo(
    () => !readonly && getExcludeAnyWarning(targetingParams),
    [readonly, targetingParams]
  );

  const items = useMemo(
    () =>
      Object.keys(filterEmptyParams(targetingParams))
        .map((type, index, currentArray) => {
          switch (type) {
            case ContentObjectOptions.LANGUAGE:
              return (
                <Fragment key={type}>
                  <IntersectOptionsItems
                    targetingParams={targetingParams}
                    type={type}
                    readonly={readonly}
                    onChange={onChange}
                  />
                  {index < currentArray.length - 1 && dimensionChipLine}
                </Fragment>
              );

            case ContentObjectOptions.LIVESTREAM:
              return (
                <Fragment key={type}>
                  <DefaultItems targetingParams={targetingParams} type={type} readonly={readonly} onChange={onChange} />
                  {index < currentArray.length - 1 && dimensionChipLine}
                </Fragment>
              );

            case ContentObjectOptions.CONTENT_RATING:
            case ContentObjectOptions.GENRE:
              return (
                <Fragment key={type}>
                  <HierarchyItems
                    targetingParams={targetingParams}
                    type={type}
                    readonly={readonly}
                    onChange={onChange}
                  />
                  {index < currentArray.length - 1 && dimensionChipLine}
                </Fragment>
              );

            case ContentObjectOptions.EPISODE:
              return (
                <Fragment key={type}>
                  <EpisodeItems targetingParams={targetingParams} readonly={readonly} onChange={onChange} />
                  {index < currentArray.length - 1 && dimensionChipLine}
                </Fragment>
              );

            case ContentObjectOptions.NETWORK:
            case ContentObjectOptions.CHANNEL:
            case ContentObjectOptions.SERIES:
              return (
                <Fragment key={type}>
                  <IntersectItems
                    targetingParams={targetingParams}
                    type={type}
                    readonly={readonly}
                    onChange={onChange}
                  />
                  {index < currentArray.length - 1 && dimensionChipLine}
                </Fragment>
              );

            default:
              return null;
          }
        })
        .filter(Boolean),
    [targetingParams, readonly, onChange, dimensionChipLine]
  );

  if (!readonly && !items.length) {
    return <EmptyFilterMessage text={t('No filters applied.')} />;
  }

  return (
    <>
      {!readonly && (
        <Box sx={{ marginTop: theme => theme.spacing(3) }}>
          <Switch
            switchState={switchState}
            onSwitchChange={() => {
              onChange(prevState => ({
                ...prevState,
                [ContentObjectOptions.CONTENT_INTER_DIMENSION_OPERATOR]: switchState ? Criteria.ALL : Criteria.ANY,
              }));
            }}
            switchOffLabel={t('MATCH ALL')}
            switchOnLabel={t('MATCH ANY')}
          />
        </Box>
      )}
      {shouldDisplayAlert && <ExcludeAnyAlert />}
      {items}
    </>
  );
};
